import {
  BarChart3,
  BookOpen,
  Brain,
  ClipboardList,
  CreditCard,
  Crown,
  DollarSign,
  FileText,
  Gift,
  Heart,
  Lightbulb,
  ListVideo,
  Mail,
  MessageSquare,
  Mic,
  Activity,
  Settings,
  Sparkles,
  Target,
  Users,
  Video,
} from 'lucide-react';

export const COMMAND_CENTER_HOME_TAB = 'overview';

export const ADMIN_ROOMS = [
  {
    id: 'marketing',
    title: 'Marketing',
    question: 'What content is in flight, and where has it been distributed?',
    supportingLine: 'YouTube-first content waterfall — track every piece from idea to distributed.',
    icon: Mail,
    primaryTab: 'marketingcontent',
    tools: [
      { tab: 'marketingcontent', label: 'Content Queue', icon: Video },
      { tab: 'marketingintelligence', label: 'Audience Intelligence', icon: Brain },
      { tab: 'marketingplaylist', label: 'Playlist Intelligence', icon: ListVideo },
    ],
    emptyPrompt: 'Add your first content piece to start tracking the waterfall.',
  },
  {
    id: 'free-users',
    title: 'Free Users',
    question: 'Which free-tier or unactivated relationships should become clearer next?',
    supportingLine: 'Activation, nurture, and conversion work will land here once the real free-user flow exists.',
    icon: Users,
    primaryTab: 'freeuserscomingsoon',
    tools: [
      { tab: 'freeuserscomingsoon', label: 'Coming Soon', icon: Users },
    ],
    emptyPrompt: 'This lane is visible now, but the free-user activation workflow still needs to be defined.',
  },
  {
    id: 'reach-dads',
    title: 'Prospects',
    question: 'Which prospects are warm enough for the next real move?',
    supportingLine: 'Prospects, guest passes, referrals, and outreach that open the door.',
    icon: Target,
    primaryTab: 'members',
    tools: [
      { tab: 'members', label: 'Prospects', icon: Users },
      { tab: 'guestpasses', label: 'Guest Passes', icon: Gift },
      { tab: 'email', label: 'Email', icon: Mail },
    ],
    emptyPrompt: 'Start by reviewing warm prospects and sending the next guest pass.',
  },
  {
    id: 'support-dads',
    title: 'Dad Partners',
    question: 'Which dads need care, follow-up, or support next?',
    supportingLine: 'Active dads, member follow-up, and support assets that meet real need.',
    icon: Heart,
    primaryTab: 'members',
    tools: [
      { tab: 'members', label: 'Dad Partners', icon: Users },
      { tab: 'email', label: 'Email', icon: Mail },
      { tab: 'feedback', label: 'Feedback', icon: MessageSquare },
      { tab: 'recordings', label: 'Recordings', icon: Video },
      { tab: 'prompts', label: 'Prompt Assistant', icon: Sparkles },
      { tab: 'gems', label: 'Gems', icon: Lightbulb },
      { tab: 'peersupport', label: 'Peer Support', icon: Heart },
      { tab: 'resources', label: 'Resources', icon: BookOpen },
    ],
    emptyPrompt: 'Start by reviewing the dads in view, then refresh the support that should actually help next.',
  },
  {
    id: 'guide-partners',
    title: 'Guide Partners',
    question: 'Who is ready to serve dads next?',
    supportingLine:
      'These are not one-time contributors. They are deeply needed examples of God’s faithfulness whose story, steadiness, and compassion can bless dads still walking through the crush of life.',
    icon: Mic,
    primaryTab: 'guestguides',
    tools: [
      { tab: 'guestguides', label: 'Guide Partners', icon: Mic },
      { tab: 'email', label: 'Email', icon: Mail },
    ],
    emptyPrompt: 'Start by reviewing who could share, coach, or serve again.',
  },
  {
    id: 'advisory-partners',
    title: 'Advisory Partners',
    question: 'Which advisory relationships or asks need a clearer next move?',
    supportingLine: 'Trusted pattern-seers, private mission asks, and the people Kevin wants close to the deeper questions.',
    icon: Crown,
    primaryTab: 'advisorycontent',
    tools: [
      { tab: 'email', label: 'Email', icon: Mail },
      { tab: 'advisorycontent', label: 'Advisory Content', icon: FileText },
      { tab: 'advisorypartneraccounts', label: 'Partner Accounts', icon: Users },
    ],
    emptyPrompt: 'Start with the private advisory update, then review who currently belongs inside the advisory circle.',
  },
  {
    id: 'support-partners',
    title: 'Support Partners',
    question: 'Which supporters need care, clarity, or trust-building next?',
    supportingLine: 'Mission supporters, sync health, and the visible impact communication that keeps support relational.',
    icon: Mail,
    primaryTab: 'supportpartneraccounts',
    tools: [
      { tab: 'email', label: 'Email', icon: Mail },
      { tab: 'supportpartneraccounts', label: 'Partner Accounts', icon: Users },
      { tab: 'supporters', label: 'Mission Support', icon: Mail },
      { tab: 'supportcontent', label: 'Support Partner Content', icon: FileText },
    ],
    emptyPrompt: 'Start by reviewing who actually has Support Partner access, then move into trust, sync health, and the next personal care move.',
  },
  {
    id: 'super-fans',
    title: 'Super Fans',
    question: 'Which super-fan relationships need a cleaner access list or next move?',
    supportingLine: 'This lane is intentionally narrow: access, fit, and lightweight relationship stewardship.',
    icon: Sparkles,
    primaryTab: 'superfanaccounts',
    tools: [
      { tab: 'superfanaccounts', label: 'Super Fan Accounts', icon: Sparkles },
    ],
    emptyPrompt: 'Start by reviewing who has Super Fan access and whether that list still reflects the right people.',
  },
  {
    id: 'steward-the-work',
    title: 'Operations',
    question: 'What business or admin work keeps Dad Partners healthy and dependable?',
    supportingLine: 'Pricing, retention, documents, and upkeep that keep the mission durable and dependable.',
    icon: DollarSign,
    primaryTab: 'analytics',
    tools: [
      { tab: 'analytics', label: 'Analytics', icon: BarChart3 },
      { tab: 'pricing', label: 'Pricing', icon: DollarSign },
      { tab: 'featureplanning', label: 'Features & Operations', icon: ClipboardList },
      { tab: 'membershipenrollments', label: 'Membership Enrollments', icon: CreditCard },
    ],
    emptyPrompt: 'Start by checking offers, retention, and the operational work that keeps the mission steady.',
  },
  {
    id: 'system-health',
    title: 'Monitoring',
    question: 'What needs attention right now across production and local watchers?',
    supportingLine: 'Monitoring stays visible here, alongside the quiet system surfaces that keep the operation dependable.',
    icon: Settings,
    primaryTab: 'monitoring',
    tools: [
      { tab: 'monitoring', label: 'Monitoring', icon: Activity },
      { tab: 'documents', label: 'Documents', icon: FileText },
      { tab: 'sitesettings', label: 'Site Settings', icon: Settings },
    ],
    emptyPrompt: 'Start by checking production health, then move into documents and settings that keep the operation stable.',
  },
];

const ROOM_BY_ID = new Map(ADMIN_ROOMS.map((room) => [room.id, room]));
const PRIMARY_ROOM_BY_TAB = new Map();

for (const room of ADMIN_ROOMS) {
  for (const tool of room.tools) {
    if (!PRIMARY_ROOM_BY_TAB.has(tool.tab)) {
      PRIMARY_ROOM_BY_TAB.set(tool.tab, room);
    }
  }
}

export const DEFAULT_ROOM_ID = ADMIN_ROOMS[0].id;

export function getAdminRoomById(roomId) {
  return ROOM_BY_ID.get(roomId) || null;
}

export function getPrimaryRoomForTab(tab) {
  return PRIMARY_ROOM_BY_TAB.get(tab) || null;
}
